import { z } from 'zod';
import { MAX_NAME_LENGTH, SETTING_LIMITS } from './constants.js';
import { GamePhaseSchema, SpecialRoleTogglesSchema } from './domain.js';
import { ErrorCodeSchema } from './errors.js';
import { PublicRoomSummarySchema, RoomEventSchema, RoomViewSchema } from './view.js';

const PlayerNameSchema = z
  .string()
  .min(1)
  .max(MAX_NAME_LENGTH * 2);
const IdSchema = z.string().min(1).max(64);
const MsgIdSchema = z.string().min(1).max(64);

/**
 * Jede Client-Nachricht darf eine `msgId` tragen.
 *
 * Der Client puffert nicht zustellbare Nachrichten und schickt sie nach einem
 * Reconnect erneut; der Server erkennt Wiederholungen an dieser Id und
 * verarbeitet sie genau einmal. Optional, damit ältere Clients gültig bleiben.
 */
const cmd = <T extends z.ZodRawShape>(shape: T) =>
  z.object({ ...shape, msgId: MsgIdSchema.optional() });

const optionalSeconds = (key: keyof typeof SETTING_LIMITS) =>
  z.number().int().min(SETTING_LIMITS[key].min).max(SETTING_LIMITS[key].max).optional();

/** Nur die Felder, die tatsächlich geändert werden sollen. */
export const SettingsPatchSchema = z.object({
  mafiaCount: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  startCountdownSeconds: optionalSeconds('startCountdownSeconds'),
  discussionSeconds: optionalSeconds('discussionSeconds'),
  votingSeconds: optionalSeconds('votingSeconds'),
  tieBreakerSeconds: optionalSeconds('tieBreakerSeconds'),
  maxTieRounds: optionalSeconds('maxTieRounds'),
  allowAbstain: z.boolean().optional(),
  /** Partiell, damit ein Schalter die anderen drei nicht mit überschreibt. */
  specialRoles: SpecialRoleTogglesSchema.partial().optional(),
});
export type SettingsPatch = z.infer<typeof SettingsPatchSchema>;

export const ClientMessageSchema = z.discriminatedUnion('type', [
  /** Erste Nachricht jeder Verbindung; mit Token wird die Session fortgesetzt. */
  z.object({
    type: z.literal('hello'),
    protocolVersion: z.number().int(),
    sessionToken: z.string().min(1).max(128).nullable(),
  }),
  z.object({ type: z.literal('ping'), sentAt: z.number().int() }),

  cmd({ type: z.literal('lobby:subscribe') }),
  cmd({ type: z.literal('lobby:unsubscribe') }),

  cmd({ type: z.literal('room:create'), playerName: PlayerNameSchema }),
  cmd({ type: z.literal('room:join'), roomId: IdSchema, playerName: PlayerNameSchema }),
  cmd({ type: z.literal('room:leave') }),
  /**
   * Löst den Raum für alle auf — nur der Host, dafür in jeder Phase.
   *
   * Bewusst kein Engine-Command: Das Auflösen ist keine Spielregel, sondern das
   * Ende der Laufzeit dieses Raums. Die Engine kennt keinen Zustand „aufgelöst".
   */
  cmd({ type: z.literal('room:close') }),
  cmd({ type: z.literal('room:kick'), playerId: IdSchema }),
  cmd({ type: z.literal('room:setLocked'), locked: z.boolean() }),

  /**
   * Sucht verwaiste Plätze, die auf diesen Namen laufen.
   *
   * Bewusst eine Suche über den Namen und keine Liste laufender Räume: Die
   * öffentliche Lobby zeigt weiterhin ausschließlich offene Spiele (GDD §31),
   * und wer nicht ohnehin weiß, wie jemand in einem Raum heißt, erfährt durch
   * diese Abfrage nichts.
   */
  cmd({ type: z.literal('rejoin:lookup'), playerName: PlayerNameSchema }),
  /**
   * Fordert einen verwaisten Platz in einem Raum zurück, ohne gültiges Token
   * (Gerätewechsel, gelöschter Speicher). Braucht die Zustimmung des Hosts —
   * siehe `rejoin:approve`.
   */
  cmd({ type: z.literal('rejoin:request'), roomId: IdSchema, playerName: PlayerNameSchema }),
  cmd({ type: z.literal('rejoin:cancel') }),
  cmd({ type: z.literal('rejoin:approve'), requestId: IdSchema }),
  cmd({ type: z.literal('rejoin:deny'), requestId: IdSchema }),

  cmd({ type: z.literal('settings:update'), patch: SettingsPatchSchema }),
  cmd({ type: z.literal('game:start') }),
  cmd({ type: z.literal('game:reset') }),
  cmd({ type: z.literal('role:acknowledge') }),
  cmd({ type: z.literal('round:startVoting') }),
  /** `targetId === null` ist eine Enthaltung. */
  cmd({ type: z.literal('vote:cast'), targetId: IdSchema.nullable() }),
  /**
   * Setzt die eigene Sonderrollen-Fähigkeit ein.
   *
   * Bewusst eine Nachricht für alle vier Rollen: welche Fähigkeit gemeint ist,
   * leitet der Server aus der Rolle des Absenders ab — genau wie `vote:cast` den
   * Wähler aus der Session ableitet. Ein Client kann damit strukturell keine
   * Fähigkeit behaupten, die er nicht besitzt.
   *
   * `targetId === null` heißt „ohne Ziel" (Doppelstimme, Letztes Wort).
   */
  cmd({ type: z.literal('ability:use'), targetId: IdSchema.nullable() }),
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

/**
 * Alles außer Handshake und Herzschlag — nur diese Nachrichten laufen über die
 * Outbox des Clients und die Wiederholungserkennung des Servers. `hello` und
 * `ping` gehören zur Verbindung selbst und werden nie nachgereicht.
 */
export type ReliableClientMessage = Exclude<
  ClientMessage,
  { type: 'hello' } | { type: 'ping' }
>;

export const ServerMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('hello:ok'),
    protocolVersion: z.number().int(),
    sessionToken: z.string(),
    playerId: z.string(),
    serverNow: z.number().int(),
    /**
     * Der Raum, in dem diese Session steckt — `null` heißt „in keinem Raum".
     *
     * Ohne diese Angabe konnte ein Client mit abgelaufenem Token auf einem
     * Spielbildschirm hängen bleiben: Er bekam ein `hello:ok` für eine frische
     * Identität, aber nichts, was seine veraltete Ansicht widerlegt hätte.
     */
    roomId: z.string().nullable(),
  }),
  /** Bestätigt eine Client-Nachricht; der Client darf sie aus der Outbox nehmen. */
  z.object({ type: z.literal('ack'), msgId: z.string() }),
  /**
   * Diese Verbindung wurde von einer neueren derselben Session verdrängt
   * (zweiter Tab). Der Client stellt seine Reconnect-Versuche daraufhin ein —
   * sonst verdrängen sich zwei Tabs endlos gegenseitig.
   */
  z.object({ type: z.literal('session:takeover') }),
  z.object({
    type: z.literal('pong'),
    /** Unverändert zurückgespiegelt, damit der Client die Roundtrip-Zeit misst. */
    sentAt: z.number().int(),
    serverNow: z.number().int(),
  }),
  z.object({ type: z.literal('lobby:list'), rooms: z.array(PublicRoomSummarySchema) }),
  z.object({ type: z.literal('room:state'), view: RoomViewSchema }),
  /** `CLOSED` = der Host hat aufgelöst; `LEFT` bekommt, wer selbst gegangen ist. */
  z.object({
    type: z.literal('room:closed'),
    reason: z.enum(['LEFT', 'KICKED', 'ROOM_GONE', 'CLOSED']),
  }),
  z.object({ type: z.literal('room:event'), event: RoomEventSchema }),

  /** Antwort auf `rejoin:lookup` — verwaiste Plätze, die auf den Namen passen. */
  z.object({
    type: z.literal('rejoin:options'),
    options: z.array(
      z.object({
        roomId: z.string(),
        roomName: z.string(),
        playerName: z.string(),
        phase: GamePhaseSchema,
        playerCount: z.number().int().nonnegative(),
      }),
    ),
  }),
  /** Stand der eigenen Wiedereinstiegs-Anfrage. */
  z.object({
    type: z.literal('rejoin:pending'),
    requestId: z.string(),
    status: z.enum(['PENDING', 'APPROVED', 'DENIED', 'EXPIRED', 'CANCELLED']),
  }),
  /** Offene Anfragen — nur an die, die sie bestätigen dürfen. */
  z.object({
    type: z.literal('rejoin:requests'),
    requests: z.array(
      z.object({
        requestId: z.string(),
        playerId: z.string(),
        playerName: z.string(),
        requestedAt: z.number().int(),
      }),
    ),
  }),

  /** `msgId` gesetzt, wenn der Fehler eine bestimmte Nachricht endgültig ablehnt. */
  z.object({ type: z.literal('error'), code: ErrorCodeSchema, msgId: z.string().optional() }),
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

export type RoomClosedReason = Extract<ServerMessage, { type: 'room:closed' }>['reason'];
export type RejoinOption = Extract<ServerMessage, { type: 'rejoin:options' }>['options'][number];
export type RejoinRequest = Extract<ServerMessage, { type: 'rejoin:requests' }>['requests'][number];
export type RejoinStatus = Extract<ServerMessage, { type: 'rejoin:pending' }>['status'];

export function parseClientMessage(raw: unknown): ClientMessage | null {
  const result = ClientMessageSchema.safeParse(raw);
  return result.success ? result.data : null;
}
